/**
 * EvidenceBuilder — 사후 분쟁 대응용 4종 증거 컨텍스트(EvidenceContext)
 * 를 생성하는 헬퍼.
 *
 * AI Proctor 운영 6원칙 중 #2(오탐 전제 — 판정이 아닌 증거 기록)와
 * #6(공정성·분쟁 대응 — 시간 + 행동 유형 + 시스템 상태 + 화면 이벤트
 * 4종을 동시 기록)에 대응한다. 단정적 boolean 대신 윈도우/문맥과
 * 함께 기록되어, 이의제기 시 설명 가능한 근거로 활용 가능해야 한다.
 *
 * 화면 visibility/focus 추적은 모듈 레벨 싱글턴이 처리한다 — App
 * 마운트 시 attachScreenWatcher() 1회 호출로 활성화된다.
 */

import type {
  BehaviorType,
  EvidenceContext,
  Phase,
  Mode,
  RiskLevel,
  ScreenContext,
  Severity,
  SystemStateSnapshot,
  EventLevel,
} from '../types';

// ── Screen visibility/focus tracker (module-level singleton) ───────

const screenState: ScreenContext = {
  visibility: 'visible',
  focused: true,
  lastChangeT: 0,
};

let screenWatcherAttached = false;

export function attachScreenWatcher(): () => void {
  if (typeof window === 'undefined' || screenWatcherAttached) {
    return () => {};
  }
  screenWatcherAttached = true;

  // 초기값 캡처 — 페이지 이미 hidden/blur 상태일 수 있음.
  if (typeof document !== 'undefined') {
    screenState.visibility = document.visibilityState === 'hidden' ? 'hidden' : 'visible';
  }
  screenState.focused = document?.hasFocus?.() ?? true;

  const onVisibility = () => {
    screenState.visibility = document.visibilityState === 'hidden' ? 'hidden' : 'visible';
    screenState.lastChangeT = performance.now();
  };
  const onFocus = () => {
    screenState.focused = true;
    screenState.lastChangeT = performance.now();
  };
  const onBlur = () => {
    screenState.focused = false;
    screenState.lastChangeT = performance.now();
  };

  document.addEventListener('visibilitychange', onVisibility);
  window.addEventListener('focus', onFocus);
  window.addEventListener('blur', onBlur);

  return () => {
    document.removeEventListener('visibilitychange', onVisibility);
    window.removeEventListener('focus', onFocus);
    window.removeEventListener('blur', onBlur);
    screenWatcherAttached = false;
  };
}

export function getScreenContext(): ScreenContext {
  return { ...screenState };
}

// ── System state snapshot ──────────────────────────────────────────

export interface SystemStateInputs {
  phase: Phase;
  mode?: Mode;
  recordingMode: boolean;
  /** Monitoring 시작 시각(performance.now 기준 ms). 0/null이면 미시작. */
  monitoringStartT?: number | null;
  faceVisible: boolean;
  /** 현재 시각 — 외부에서 주입(테스트 친화). 미지정 시 performance.now. */
  now?: number;
}

export function buildSystemState(inputs: SystemStateInputs): SystemStateSnapshot {
  const now = inputs.now ?? performance.now();
  const monitoringSec =
    inputs.monitoringStartT && inputs.phase === 'monitoring'
      ? Math.max(0, (now - inputs.monitoringStartT) / 1000)
      : 0;
  return {
    phase: inputs.phase,
    mode: inputs.mode,
    recordingMode: inputs.recordingMode,
    monitoringSec,
    faceVisible: inputs.faceVisible,
  };
}

// ── Risk level mapping ─────────────────────────────────────────────

export function severityToRisk(severity: Severity): RiskLevel {
  switch (severity) {
    case 'CRITICAL': return 'high';
    case 'WARNING':  return 'med';
    case 'NORMAL':   return 'low';
  }
}

export function eventLevelToRisk(level: EventLevel): RiskLevel {
  switch (level) {
    case 'fusion':  return 'high';
    case 'anomaly': return 'high';
    case 'warning': return 'med';
    case 'normal':  return 'low';
  }
}

// ── EvidenceContext composition ────────────────────────────────────

export function buildEvidence(
  behaviorType: BehaviorType,
  systemInputs: SystemStateInputs,
): EvidenceContext {
  return {
    behaviorType,
    systemState: buildSystemState(systemInputs),
    screenContext: getScreenContext(),
  };
}
